/**
 * Memoise an async factory's SUCCESS only.
 *
 * A plain `cached ??= factory()` also caches rejections, which for a lazily
 * imported chunk means one network blip pins the rest of the pageview to the
 * failure path. Clearing on rejection restores retry on the next call.
 *
 * This lives in its own module because its caller holds a dynamic `import()` and
 * is therefore unreachable from jest — a branch in there would be untested.
 */
export function memoizeAsync<T>(factory: () => Promise<T>): () => Promise<T> {
  let cached: Promise<T> | null = null;

  return () => {
    if (cached != null) return cached;

    const pending = factory();
    cached = pending;
    // Guarded so a retry already in flight is not discarded by a late rejection
    // from the attempt it replaced.
    pending.catch(() => {
      if (cached === pending) cached = null;
    });

    return pending;
  };
}

export default memoizeAsync;
